import type { SupabaseClient } from '@supabase/supabase-js'
import type { z } from 'zod'
import { depotSchema, routePathCommandResultSchema, routePathViewSchema } from '@groupe-reca/contracts'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import { RoutePathError, type RoutePathRepository } from '../../application/RoutePathRepository'

function rpcErrorCode(message: string | undefined): string {
  return /[A-Z][A-Z0-9_]{3,}/.exec(message ?? '')?.[0] ?? 'UNEXPECTED'
}

/** Fonctions de la migration 20261118010000_route_paths_v1. */
export function createSupabaseRoutePaths(client: AppSupabaseClient): RoutePathRepository {
  const rpcClient = client as unknown as SupabaseClient

  async function rpc<T>(
    name: string,
    args: Record<string, unknown> | null,
    schema: z.ZodType<T>,
  ): Promise<T> {
    let response: { data: unknown; error: { message?: string } | null }
    try {
      response = (await (args === null ? rpcClient.rpc(name) : rpcClient.rpc(name, args))) as typeof response
    } catch {
      throw new RoutePathError('NETWORK')
    }
    if (response.error) throw new RoutePathError(rpcErrorCode(response.error.message))
    const parsed = schema.safeParse(response.data)
    if (!parsed.success) throw new RoutePathError('RESPONSE_INVALID')
    return parsed.data
  }

  return {
    getView: (routeId) => rpc('route_path_view_v1', { p_route_id: routeId }, routePathViewSchema),
    save: (command) => rpc('save_route_path_v1', { p_command: command }, routePathCommandResultSchema),
    validate: (command) =>
      rpc('validate_route_path_v1', { p_command: command }, routePathCommandResultSchema),
    getDepot: () => rpc('route_depot_v1', null, depotSchema.nullable()),
  }
}
